import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { type BalanceReferenceDto, getBalanceReferenceFromConnect } from '@/lib/connect-api';
import { getMockBalanceReference } from '@/lib/mockData';

interface BalanceReferenceState {
  reference: BalanceReferenceDto | null;
  /** Debt under an active payment plan is left out of the amount. */
  excludesArrangedDebts: boolean;
  /** The read succeeded, so the shared reference is enabled for this client.
   *  A null `reference` alone can't tell "enabled, no code issued yet" apart
   *  from "not available here", and only the former may be offered as a method. */
  isEnabled: boolean;
  /** The account this was read for. A reference is bound to one account's
   *  balance and cannot be retracted, so it must never be rendered under a
   *  different account — see the render-time guard below. */
  readFor: string | null;
}

const NOTHING: BalanceReferenceState = {
  reference: null,
  excludesArrangedDebts: false,
  isEnabled: false,
  readFor: null,
};

export interface BalanceReferenceRead extends Omit<BalanceReferenceState, 'readFor'> {
  /** Re-read after a mint. A first-ever mint leaves this hook's state empty, so
   *  without it the card renders nothing beside the code just issued. */
  refetch: () => void;
}

/**
 * Reads the account's current Multibanco balance reference. Never mints.
 *
 * Every failure — feature off (404), no account context, network error —
 * collapses to `{reference: null, isEnabled: false}`, so callers can render
 * nothing rather than surface an error the customer cannot act on.
 *
 * Call this once per screen and pass the result down: two components each
 * calling it would issue two identical reads per render.
 */
export function useBalanceReference(): BalanceReferenceRead {
  const { accountId, authStage, getAccessToken } = useAuth();
  const { useMockData } = useConfiguration();
  const [state, setState] = useState<BalanceReferenceState>(NOTHING);
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    if (useMockData) {
      const mock = getMockBalanceReference();
      setState({
        reference: mock.reference,
        excludesArrangedDebts: mock.excludes_arranged_cases,
        isEnabled: true,
        readFor: accountId,
      });
      return;
    }
    if (!accountId || (authStage !== 'limited' && authStage !== 'full')) {
      setState(NOTHING);
      return;
    }
    let active = true;
    getBalanceReferenceFromConnect(authStage, getAccessToken)
      .then((result) => {
        if (!active) return;
        setState({
          reference: result.reference,
          excludesArrangedDebts: result.excludes_arranged_cases,
          isEnabled: true,
          readFor: accountId,
        });
      })
      .catch(() => {
        if (active) setState({ ...NOTHING, readFor: accountId });
      });
    return () => {
      active = false;
    };
    // `accountId` is a dependency so switching account tears down an in-flight
    // read and starts a fresh one: the portal can swap account under a mounted
    // tree (another tab signs in with a different access code) without
    // `authStage` changing at all.
  }, [useMockData, accountId, authStage, getAccessToken, reloads]);

  // Guard at render, not only in the effect: effects run after paint, so an
  // account switch would otherwise show the previous account's code for a frame.
  // The amount is bound to that account's balance and the code cannot be
  // retracted, so one frame is enough to send a customer to pay the wrong debt.
  const current = state.readFor === accountId ? state : NOTHING;

  return {
    reference: current.reference,
    excludesArrangedDebts: current.excludesArrangedDebts,
    isEnabled: current.isEnabled,
    refetch,
  };
}
